import type { AiValidationResult } from './ai-validation';

export const SearchTermStatus = {
  Pending: "pending",
  Generating: "generating",
  Completed: "completed",
  Failed: "failed",
  Refreshing: "refreshing",
} as const;

export type SearchTermStatusType = (typeof SearchTermStatus)[keyof typeof SearchTermStatus];

export type SearchTermOrigin = {
  keyword: string;
  original_phrase: string;
  original_phrase_search_volume: number;
};

export type SearchTermEntry = {
  id: number;
  slot_index: number;
  content: string;
  char_limit: number;
};

export type SearchTermSet = {
  id: number;
  product_id: number;
  type: "single" | "grouped";
  status: SearchTermStatusType;
  status_info?: string | null;
  created_at: string;
  updated_at: string;
  entries: SearchTermEntry[];
  origins: SearchTermOrigin[];
  validation?: AiValidationResult | null;
  // Three claim keys from `POST …/{id}/regenerate-slots` (`components/search-term.md
  // § GET /api/search-term-sets gains three keys per set`). Optional because the public
  // share payload (`PublicSearchTermSetView` → `SearchTermsContentV2`) reuses this same
  // type and never carries them. `regeneration_started_at` / `regeneration_failed_at` are
  // ISO-8601 UTC with a trailing `Z`, unlike `created_at` / `updated_at` on this same type,
  // which stay the warehouse's raw `TIMESTAMP_NTZ` string -- do not "fix" that here.
  regeneration_slots?: number[] | null;
  regeneration_started_at?: string | null;
  regeneration_failed_at?: string | null;
};

/**
 * `POST /api/search-term-sets/{id}/regenerate-slots` request body
 * (`components/search-term.md § The seventh endpoint`). The request key is
 * `slot_indices`; the response/column/type name is `regeneration_slots` --
 * deliberately not the same word, see the same section.
 */
export type RegenerateSearchTermSlotsRequest = {
  slot_indices: number[];
};

/**
 * `202` body from the same endpoint -- exactly these three keys, in request
 * order for `regeneration_slots` (not sorted). The SPA's FR-8 toast-count
 * snapshot is seeded from this response, not from a persisted result.
 */
export type RegenerateSearchTermSlotsResponse = {
  id: number;
  regeneration_slots: number[];
  regeneration_started_at: string;
};
